import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { tripHoldReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * Raw GPS points upload (T132, docs/specs/sync-backend-plan.md section 2.3). The phone packs the
 * points of a trip into one gzip file, asks the API for a signed upload URL, PUTs the file to
 * the private bucket `trip-raw-points`, then pushes the track row with the path and the claim
 * (sha256, bytes, points count) that the server verifies against the stored object.
 */

/** The largest raw points file: 5 MiB. Larger is answered with 413 PAYLOAD_TOO_LARGE. */
export const RAW_POINTS_MAX_BYTES = 5 * 1024 * 1024
/** The most points one file may hold. Larger is answered with 413 PAYLOAD_TOO_LARGE. */
export const RAW_POINTS_MAX_POINTS = 50_000
export const RAW_POINTS_CONTENT_TYPE = "application/gzip"
/** Supabase Storage signed upload URLs are valid for 2 hours (not configurable). */
export const RAW_POINTS_UPLOAD_URL_TTL_SECONDS = 7200

/** Per-user limit on POST /trip-tracks/:id/raw-points over a fixed one-minute window; more is 429. */
export const RAW_POINTS_UPLOAD_RATE_LIMIT = {
  windowSeconds: 60,
  requestsPerWindow: 30,
} as const
/**
 * Soft per-organisation budget for stored raw points files: 200 MiB. Today exceeding it is only
 * logged (owner decision, T182); no request is refused.
 */
export const RAW_POINTS_ORG_QUOTA_BYTES = 200 * 1024 * 1024

/** Lower-case hex SHA-256 of the file bytes. */
const rawPointsSha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "must be 64 lower-case hex characters")

/**
 * The object path inside the private bucket: `<organisation_id>/<trip_id>/<sha256>.jsonl.gz`.
 * The server issues it and accepts no other path on a track row, so a signed URL can never
 * reach another organisation's folder and equal content is stored once per trip.
 */
export function rawPointsObjectPath(
  organisationId: string,
  tripId: string,
  sha256: string,
): string {
  return `${organisationId}/${tripId}/${sha256}.jsonl.gz`
}

/**
 * POST /api/v1/trip-tracks/:trip_id/raw-points. The limits are `.max()` here for the JSON
 * schema, and a body over them is answered with 413 (not 422) by the route.
 */
export const rawPointsUploadRequestSchema = z.object({
  sha256: rawPointsSha256Schema,
  bytes: z.number().int().min(1).max(RAW_POINTS_MAX_BYTES),
  points_count: z.number().int().min(1).max(RAW_POINTS_MAX_POINTS),
  content_type: z.literal(RAW_POINTS_CONTENT_TYPE),
})

export type RawPointsUploadRequest = z.infer<typeof rawPointsUploadRequestSchema>

/**
 * Answer of the upload endpoint (lenient). `already_uploaded` is true when this trip already
 * holds an object with this sha256 and size: nothing to upload, `upload_url` and `expires_at`
 * are null. Otherwise PUT the file bytes to `upload_url` with `Content-Type: application/gzip`
 * before `expires_at`, then push the track row.
 */
export const rawPointsUploadResponseSchema = z.object({
  path: z.string(),
  upload_url: z.string().nullable(),
  expires_at: z.string().datetime({ offset: true }).nullable(),
  already_uploaded: z.boolean(),
})

export type RawPointsUploadResponse = z.infer<typeof rawPointsUploadResponseSchema>

/** What a track row says about its raw points file (all three travel with the path). */
interface RawPointsClaimFields {
  raw_points_path?: string | null | undefined
  raw_points_sha256?: string | null | undefined
  raw_points_bytes?: number | null | undefined
  raw_points_count?: number | null | undefined
}

/**
 * Path and claim go together: a path needs sha256, bytes and count so the server can verify
 * the object; without a path (or when clearing it) none of them may be set.
 */
function checkRawPointsClaim(fields: RawPointsClaimFields, ctx: z.RefinementCtx): void {
  const claim = ["raw_points_sha256", "raw_points_bytes", "raw_points_count"] as const
  const hasPath = typeof fields.raw_points_path === "string"
  for (const key of claim) {
    const present = fields[key] !== undefined && fields[key] !== null
    if (hasPath && !present) {
      ctx.addIssue({ code: "custom", path: [key], message: "required with raw_points_path" })
    }
    if (!hasPath && present) {
      ctx.addIssue({ code: "custom", path: [key], message: "only allowed with raw_points_path" })
    }
  }
}

/**
 * Route track storage schema (s4-trips-api-brief §2.3).
 * Separates high-frequency location point data from the trips table.
 * Standardized with document synchronization metadata.
 */
export const tripTrackSchema = z.object({
  trip_id: z.uuid(),
  organisation_id: z.uuid(),
  display_polyline: z.string().nullable(),
  raw_points_path: z.string().nullable(),
  // T132: what the file was verified against; null when no file or the row predates T132.
  raw_points_sha256: z.string().nullable().default(null),
  raw_points_bytes: z.number().int().nullable().default(null),
  raw_points_count: z.number().int().nullable().default(null),
  points_retention_until: z.string().datetime({ offset: true }).nullable(),
  algorithm_version: z.string().min(1),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: tripHoldReasonSchema.nullable().default(null),
  revision: revisionSchema.default(0),
  deleted_at: z.string().datetime({ offset: true }).nullable().default(null),
  created_at: z.string().datetime({ offset: true }),
})

export type TripTrack = z.infer<typeof tripTrackSchema>

/**
 * Request payload to create or update a trip track record.
 */
export const createTripTrackRequestSchema = z
  .object({
    trip_id: z.uuid(),
    display_polyline: z.string().nullable().optional().default(null),
    raw_points_path: z.string().nullable().optional().default(null),
    raw_points_sha256: rawPointsSha256Schema.nullable().optional().default(null),
    raw_points_bytes: z
      .number()
      .int()
      .min(1)
      .max(RAW_POINTS_MAX_BYTES)
      .nullable()
      .optional()
      .default(null),
    raw_points_count: z
      .number()
      .int()
      .min(1)
      .max(RAW_POINTS_MAX_POINTS)
      .nullable()
      .optional()
      .default(null),
    points_retention_until: z
      .string()
      .datetime({ offset: true })
      .nullable()
      .optional()
      .default(null),
    algorithm_version: z.string().min(1).default("v1"),
  })
  .superRefine(checkRawPointsClaim)

export type CreateTripTrackRequest = z.infer<typeof createTripTrackRequestSchema>

/**
 * Request payload to update a trip track through sync push (patch: only changed fields).
 * `points_retention_until` is server-owned and is not accepted here.
 */
export const updateTripTrackRequestSchema = z
  .object({
    base_revision: z.number().int().nonnegative().optional(),
    display_polyline: z.string().nullable().optional(),
    raw_points_path: z.string().nullable().optional(),
    raw_points_sha256: rawPointsSha256Schema.nullable().optional(),
    raw_points_bytes: z.number().int().min(1).max(RAW_POINTS_MAX_BYTES).nullable().optional(),
    raw_points_count: z.number().int().min(1).max(RAW_POINTS_MAX_POINTS).nullable().optional(),
    algorithm_version: z.string().min(1).optional(),
  })
  .superRefine(checkRawPointsClaim)

export type UpdateTripTrackRequest = z.infer<typeof updateTripTrackRequestSchema>
